import { useSnackbar } from "notistack";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { navigate } from "gatsby";
import {
  getUserCounters,
  getUserInfo,
  updateUserInfo,
  validateEmail,
  validatePhoneNumber,
  validateEmailPasswordResetRequired,
  deleteUserByEmail,
  deleteUsersByEmail,
} from "../api-services/users";
import { cognitoChangePassword, isAuthenticatedUser } from "../utils/user-auth-provider";
import { QUERY_CACHE_TIME, USER_ROLE } from "../commons/enum";
import { QUERY_KEY } from "../commons/query-key.ts";

export const useIsLoggedIn = () => {
  const { data: isLoggedIn } = useQuery("isLoggedIn", isAuthenticatedUser, {
    retry: false,
  });
  return isLoggedIn;
};

export const useMyInfo = () => {
  const isLoggedIn = useIsLoggedIn();
  const queryClient = useQueryClient();
  return useQuery(QUERY_KEY.MY_INFO, getUserInfo, {
    enabled: isLoggedIn,
    retry: false,
    onSuccess: (data) => {
      if (data?.userRole === USER_ROLE.USER) {
        navigate("/forbidden");
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries("myStaffRoles");
    },
  });
};

export const useMyUserRole = () => {
  const { data: myInfo } = useMyInfo();
  return useQuery(
    ["myUserRoles", myInfo?.id],
    () => {
      if (!myInfo) return [];
      return { userRole: myInfo.userRole, id: myInfo.id };
    },
    {
      enabled: !!myInfo,
      staleTime: 5 * 60 * 1000,
      cacheTime: QUERY_CACHE_TIME.DEFAULT,
      retry: 2,
      select: (data) => data || [],
    }
  );
};

export const useMyStaffRoles = () => {
  const { data: myInfo, isError, isLoading } = useMyInfo();
  const queryClient = useQueryClient();
  return useQuery("myStaffRoles", () => myInfo?.staffRoles || [], {
    onSettled: () => {
      queryClient.invalidateQueries("isCompanyAdmin");
    },
    enabled: !isLoading && !isError,
  });
};

export const useIsCompanyAdmin = () => {
  const { data: staffRoles, isLoading, isError } = useMyStaffRoles();
  return useQuery(
    "isCompanyAdmin",
    () => {
      return staffRoles?.includes("COMPANY_ADMIN");
    },
    {
      enabled: !isLoading && !isError, // Only run this query if staffRoles are loaded and there's no error
    }
  );
};

export const useIsSystemAdmin = () => {
  const { data: myInfo, isLoading, isError } = useMyInfo();
  return useQuery(
    "isSystemAdmin",
    () => {
      return myInfo?.userRole === USER_ROLE.ADMIN;
    },
    {
      enabled: !isLoading && !isError,
    }
  );
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation((updateUserInfoParams) => updateUserInfo(updateUserInfoParams), {
    onSuccess: () => {
      queryClient.invalidateQueries("myInfo");
      enqueueSnackbar("Your information has been updated", {
        variant: "success",
      });
    },
  });
};

export const useClearQueryCache = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.clear();
  };
};

export const useDeleteUserByEmail = ({ onSuccess }) => {
  return useMutation(deleteUserByEmail, {
    onSuccess,
  });
};

export const useDeleteUsersByEmail = ({ onSuccess }) => {
  return useMutation(deleteUsersByEmail, {
    onSuccess,
  });
};

export const useChangePassword = ({ onSuccess, onError }) => {
  return useMutation(
    ({ currentPassword, newPassword }) => cognitoChangePassword(currentPassword, newPassword),
    {
      onSuccess,
      onError,
    }
  );
};

export const useValidateEmailPasswordResetRequired = () => {
  return useMutation((email) => validateEmailPasswordResetRequired(email));
};

export const useValidateEmail = () => {
  return useMutation((email) => validateEmail(email));
};

export const useValidatePhoneNumber = () => {
  return useMutation((phoneNumber) => validatePhoneNumber(phoneNumber));
};

export const useGetUserCounters = () => {
  return useQuery("userCounters", getUserCounters);
};
